import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router';
import {
    AccordionFullTable,
    AddCircleBlue,
    CustomDelete,
    CustomizedSwitch,
    DownloadCsv,
    ManageActions,
    SimpleTooltip,
} from 'components';
import {PermissionList, RolePermission, tagEnums, useModal, useSecondaryModal} from 'utils';
import {domainActions, goalActions, tagActions} from 'store';
import {GoalsFromLibrary} from './goalsFromLibrary';
import {PostedNoteAction} from './postedNote';
import {goalTableInfo, goalStatusEnums, DELETE_GOAL_ACTION_TYPE, RESTORE_GOAL_ACTION_TYPE} from './constants';
import { EditTransitionDate } from './editTransitionDate';
import {AddGoal} from './addGoal';
import {GoalDetails} from './goalDetails';
import {ClinicalInsightsDrawer} from './clinicalInsights';
// Direct path, not the `fragments` barrel — goals is itself under fragments, so the barrel would cycle.
import {AddManualData} from '../../../../clinicalFragments/manualSession';
import {Svg} from 'assets';
import {useLocation} from 'react-router-dom';
import InsightsIcon from '@mui/icons-material/Insights';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {IconButton, Tooltip} from '@mui/material';

const goalToolbarIconSx = {
    width: 36,
    height: 36,
    border: '1px solid #D0D5DD',
    borderRadius: '8px',
    background: '#fff',
    '&:hover': {background: '#F9FAFB', borderColor: '#0C66E4'},
};

export const Goals = ({}) => {
    const client = useSelector((state) => state.client.clientItemInfo);
    const goalList = useSelector((state) => state.goals.goalList);
    const domainList = useSelector((state) => state.domains.domainList);
    const dispatch = useDispatch();
    const params = useParams();
    const location = useLocation();
    const info = location?.state;
    const {open} = useModal();
    const {openSecondary} = useSecondaryModal();
    const [expandedGoalIds, setExpandedGoalIds] = useState(() => new Set());
    const [insightsOpen, setInsightsOpen] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);

    const renderParams = () => {
        const pushInfo = {
            ...info,
            clientId: params?.id,
        };

        if (info?.statuses?.length) {
            pushInfo.statuses = info?.statuses;
        } else {
            delete pushInfo.statuses;
        }

        if (showDeleted) {
            pushInfo.includeDeleted = true;
        } else {
            delete pushInfo.includeDeleted;
        }

        // if (!pushInfo.statuses) {
        //     pushInfo.statuses = defaultStatuses;
        // }
        delete pushInfo.page;
        delete pushInfo.activeTab;
        return pushInfo;
    };

    useEffect(() => {
        dispatch(tagActions.getTags({module: tagEnums.GOAL}));
        dispatch(domainActions.getDomains());
    }, [dispatch]);

    useEffect(() => {
        getGoals();
    }, [info, showDeleted]);

    const getGoals = (load) => {
        dispatch(goalActions.getGoals(renderParams(), load));
    };

    const toggleGoal = (goalId, isExpanded) => {
        setExpandedGoalIds((prev) => {
            const next = new Set(prev);
            isExpanded ? next.add(goalId) : next.delete(goalId);
            return next;
        });
    };

    const handleClickButton = (item, name, i) => {
        // Expanding is display-only, so deleted goals can still be expanded to view their targets.
        if (name === 'dropDown') {
            return toggleGoal(item?.id, !expandedGoalIds.has(item?.id));
        }
        if (item?.deletedAt) {
            if (name === 'restore') {
                open(
                    <CustomDelete
                        info={`Restore "${item?.name}"?`}
                        text={'The goal comes back with its targets, options and status as they were when it was deleted.'}
                        innerText={'Restore'}
                        color={'blue'}
                        actionType={RESTORE_GOAL_ACTION_TYPE}
                        handleDel={() => dispatch(goalActions.restoreGoal(item?.id, renderParams()))}
                    />
                );
            }
            return null;
        } else {
            if (name === 'chart') {
                openSecondary(
                    <GoalDetails goalId={item?.id} clientId={client?.id} requestParams={renderParams()}
                                 defaultTab={'Goal Progress'} afterSuccess={getGoals}/>
                );
            }
            if (name === 'detail') {
                openSecondary(<GoalDetails goalId={item?.id} clientId={client?.id} requestParams={renderParams()}
                                           afterSuccess={getGoals}/>);
            }
            if (name === 'targetChart') {
                openSecondary(
                    <GoalDetails goalId={item?.id} clientId={client?.id} requestParams={renderParams()}
                                 defaultTab={'Goal Progress'} afterSuccess={getGoals}/>
                );
            }
            if (name === 'editTransitionDate') {
                open(<EditTransitionDate goalId={item?.id} target={i} requestParams={renderParams()} />);
            }
            if (name === 'targetDetail') {
                openSecondary(<GoalDetails goalId={item?.id} requestParams={renderParams()} defaultTab={'Targets'}
                                           afterSuccess={getGoals}/>);
            }
            if (name === 'clone') {
                open(<AddGoal isClone info={item} requestParams={renderParams()}
                              afterSuccess={(item) => handleClickButton(item, 'detail')}/>);
            }
            if (name === 'delete') {
                open(
                    <CustomDelete
                        info={`Are you sure you want to delete "${item?.name}"?`}
                        text={
                            <>
                                This goal will be moved to Deleted Items and <b>won't be available for new sessions</b>.
                                Existing session
                                data will remain unchanged. You can view deleted goals using the <b>"Show
                                deleted"</b> toggle.
                            </>
                        }
                        innerText={'Delete'}
                        actionType={DELETE_GOAL_ACTION_TYPE}
                        handleDel={() => dispatch(goalActions.deleteGoal(item?.id, renderParams()))}
                    />
                );
            }
        }
    };

    // Server order: the column the user picked, else the org's default goal order (Settings > Clinical).
    const sortedGoalList = Array.isArray(goalList) ? goalList : [];
    const orderedGoalList = showDeleted
        ? [...sortedGoalList].sort((a, b) => (a?.deletedAt ? 1 : 0) - (b?.deletedAt ? 1 : 0))
        : sortedGoalList;

    const deletedGoal = orderedGoalList?.find((i) => i?.deletedAt);

    const anyExpanded = orderedGoalList?.some((goal) => expandedGoalIds.has(goal?.id));
    const toggleAll = () => {
        setExpandedGoalIds(anyExpanded ? new Set() : new Set(orderedGoalList?.map((goal) => goal?.id)));
    };

    return (
        <div>
            {/*{RolePermission([PermissionList.CLIENT_CONTACT_MANAGE?.code]) &&*/}
            <div className="header-action-btn" style={{display: 'flex', alignItems: 'center'}}>
                <div className="flex-align-center" style={{gap: 20}}>
                    <PostedNoteAction clientId={params?.id}/>
                    <div className="flex-align-center" style={{gap: '8px'}}>
                        <p className="goal-show-deleted">Show deleted</p>
                        <CustomizedSwitch checked={showDeleted} handleClick={() => setShowDeleted(!showDeleted)}/>
                    </div>
                    {!!orderedGoalList?.length && (
                        <button type="button" className="goal-toggle-all-btn" onClick={toggleAll}>
                            {anyExpanded ? <UnfoldLessIcon sx={{fontSize: 18}}/> : <UnfoldMoreIcon sx={{fontSize: 18}}/>}
                            {anyExpanded ? 'Collapse all' : 'Expand all'}
                        </button>
                    )}
                </div>

                <div className="flex-end flex-align-center" style={{gap: 8}}>
                    {RolePermission([PermissionList.GOAL_CREATE?.code]) && (
                        <ManageActions
                            btnClass="reset-btn"
                            renderBtnView={(isOpen) => (
                                <div className="flex-align-center" style={{gap: 6}}>
                                    <AddCircleBlue text={'Add Goal'}/>

                                    <Svg.ArrowRight2Svg
                                        style={{
                                            transform: isOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                                            filter: 'brightness(0) saturate(100%) invert(29%) sepia(94%) saturate(1578%) hue-rotate(201deg) brightness(90%) contrast(100%)',
                                        }}
                                    />
                                </div>
                            )}
                            renderBody={(closePopover) => (
                                <div className="flex" style={{flexDirection: 'column'}}>
                                    <button
                                        className="header-add-goals-items-btn"
                                        onClick={() => {
                                            open(
                                                <AddGoal
                                                    requestParams={renderParams()}
                                                    afterSuccess={(item) => handleClickButton(item, 'detail')}
                                                />
                                            );
                                            closePopover?.();
                                        }}>
                                        Create New Goal
                                    </button>

                                    <button
                                        className="header-add-goals-items-btn"
                                        onClick={() => {
                                            open(<GoalsFromLibrary requestParams={renderParams()}/>);
                                            closePopover?.();
                                        }}>
                                        Choose from Library
                                    </button>
                                </div>
                            )}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            styleBodyItems={{padding: 0}}
                        />
                    )}

                    {RolePermission([PermissionList.SESSION_DATA_MANAGE?.code]) && (
                        <SimpleTooltip title="Add manual data" placement="top">
                            <button
                                type="button"
                                aria-label="Add manual data"
                                className="download-csv-btn-small"
                                onClick={() => open(<AddManualData clientId={params?.id}/>)}>
                                <Svg.ClipboardPlusSvg width={20} height={20}/>
                            </button>
                        </SimpleTooltip>
                    )}

                    <DownloadCsv
                        type={'goalSession'}
                        fromModal={true}
                        permissions={[PermissionList.GOAL_CREATE?.code]}
                    />

                    <Tooltip title="Clinical Insights">
                        <IconButton
                            onClick={() => setInsightsOpen(true)}
                            sx={goalToolbarIconSx}>
                            <InsightsIcon sx={{color: '#0C66E4', fontSize: 18}}/>
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
            <AccordionFullTable
                expandedIds={expandedGoalIds}
                onExpandChange={toggleGoal}
                margin={'16px 0 0 0'}
                tableInfo={goalTableInfo(renderParams(), domainList, handleClickButton, expandedGoalIds, showDeleted)}
                list={orderedGoalList}
                notShowDown={true}
                parentClick={(evt, item) => {
                    evt.stopPropagation();
                    evt.preventDefault();
                    handleClickButton(item, 'detail');
                }}
            />
            {/*{showDeleted && !deletedGoal && orderedGoalList?.length &&*/}
            {/*  <div>*/}
            {/*  </div>*/}
            {/*}*/}

            <ClinicalInsightsDrawer
                open={insightsOpen}
                onClose={() => setInsightsOpen(false)}
                clientId={client?.id}
                clientName={client?.firstName ? `${client.firstName} ${client.lastName}` : ''}
            />
        </div>
    );
};
